import React, { useEffect, useRef } from 'react';
import { createChart, ColorType, IChartApi, ISeriesApi } from 'lightweight-charts';
import { useVolume } from '../hooks/useMarketData';
import type { TimeRange } from '../types';

interface Props {
  range: TimeRange;
}

export const VolumeChart: React.FC<Props> = ({ range }) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const volumeSeriesRef = useRef<ISeriesApi<'Histogram'> | null>(null);
  const avgSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  
  const { data, isLoading } = useVolume(range);

  // Create chart once when we have data and container is ready
  useEffect(() => {
    if (!chartContainerRef.current || !data || chartRef.current) return;

    // Wait for container to have proper width
    const containerWidth = chartContainerRef.current.clientWidth;
    if (containerWidth === 0) return;

    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: 'white' },
        textColor: '#333',
      },
      width: containerWidth,
      height: 250,
      timeScale: {
        timeVisible: true,
        secondsVisible: false,
      },
      grid: {
        vertLines: { color: '#f0f0f0' },
        horzLines: { color: '#f0f0f0' },
      },
    });

    const volumeSeries = chart.addHistogramSeries({
      color: '#3b82f6',
      priceFormat: {
        type: 'volume',
      },
    });

    const avgSeries = chart.addLineSeries({
      color: '#f59e0b',
      lineWidth: 2,
      title: '20-Period Avg',
      priceLineVisible: false,
    });

    chartRef.current = chart;
    volumeSeriesRef.current = volumeSeries;
    avgSeriesRef.current = avgSeries;

    // Set initial data immediately
    if (data) {
      const volumeData = data.data.map((point) => ({
        time: Math.floor(new Date(point.timestamp).getTime() / 1000) as any,
        value: point.volume,
        color: '#3b82f6',
      }));

      const avgData = data.data
        .filter((point) => point.averageVolume !== null)
        .map((point) => ({
          time: Math.floor(new Date(point.timestamp).getTime() / 1000) as any,
          value: point.averageVolume,
        }));

      volumeSeries.setData(volumeData as any);
      avgSeries.setData(avgData as any);
      chart.timeScale().fitContent();
    }

    const handleResize = () => {
      if (chartContainerRef.current && chartRef.current) {
        chart.applyOptions({
          width: chartContainerRef.current.clientWidth,
        });
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
      }
      volumeSeriesRef.current = null;
      avgSeriesRef.current = null;
    };
  }, [data]);

  // Update data when it changes
  useEffect(() => {
    if (!data || !volumeSeriesRef.current || !avgSeriesRef.current || !chartRef.current) return;

    const volumeData = data.data.map((point) => ({
      time: Math.floor(new Date(point.timestamp).getTime() / 1000) as any,
      value: point.volume,
      color: '#3b82f6',
    }));

    const avgData = data.data
      .filter((point) => point.averageVolume !== null)
      .map((point) => ({
        time: Math.floor(new Date(point.timestamp).getTime() / 1000) as any,
        value: point.averageVolume,
      }));

    volumeSeriesRef.current.setData(volumeData as any);
    avgSeriesRef.current.setData(avgData as any);
    chartRef.current.timeScale().fitContent();
  }, [data]);

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800">Volume Analysis</h3>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
            <span className="text-sm text-gray-600">Volume</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-amber-500 rounded-full"></div>
            <span className="text-sm text-gray-600">20-Period Avg</span>
          </div>
        </div>
      </div>

      <div className="relative">
        {isLoading && (
          <div className="absolute inset-0 h-[250px] flex items-center justify-center bg-gray-50 rounded z-10">
            <div className="text-gray-500">Loading volume data...</div>
          </div>
        )}
        <div ref={chartContainerRef} className="h-[250px] w-full" />
      </div>
    </div>
  );
};
